import React from 'react';
import { Box, TextField, Checkbox, MenuItem } from '@material-ui/core';
import CreateContentField from './CreateContentField';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import { SubscriptionBoost } from './types';
import { useDebouncedState } from '../useDebouncedState';

type Props = {
  boost: SubscriptionBoost;
  index: number;
  errors: any;
  onChange: (index: number, field: keyof SubscriptionBoost, value: any) => void;
  onRemove: (index: number) => void;
};

const SubscriptionBoostRow = React.memo(
  ({ boost, index, errors, onChange, onRemove }: Props) => {
    const { value: localBoost, setValue: setLocalBoostItem } =
      useDebouncedState({
        value: boost,
        onChange: (newValue) => {
          Object.keys(newValue).forEach((key) => {
            const field = key as keyof SubscriptionBoost;
            if (newValue[field] !== boost[field]) {
              onChange(index, field, newValue[field]);
            }
          });
        },
        delay: 300,
      });

    const handleChange = (field: keyof SubscriptionBoost, value: any) => {
      setLocalBoostItem({ ...localBoost, [field]: value });
    };

    return (
      <div className='content__fields-item'>
        <div className='boost-fields-row'>
          <Box width={'100%'} display={'flex'}>
            {/* ACTIVE */}
            <CreateContentField
              fieldTitle='Active'
              field={
                <Checkbox
                  checked={localBoost.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
              }
            />

            {/* TITLE */}
            <CreateContentField
              fieldTitle='Title'
              field={
                <TextField
                  size='small'
                  variant='outlined'
                  value={localBoost.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  error={!!errors?.title}
                  helperText={errors?.title}
                />
              }
            />

            {/* LABEL */}
            <CreateContentField
              fieldTitle='Label'
              field={
                <TextField
                  variant='outlined'
                  size='small'
                  value={localBoost.label}
                  onChange={(e) => handleChange('label', e.target.value)}
                />
              }
            />
            {/* SOURCE */}
            <CreateContentField
              fieldTitle='Source'
              field={
                <TextField
                  select
                  variant='outlined'
                  size='small'
                  value={localBoost.source}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('source', e.target.value)
                  }
                >
                  <MenuItem value='ALL'>ALL</MenuItem>
                </TextField>
              }
            />
          </Box>
          <Box width={'100%'} display={'flex'}>
            {/* ORDER COST */}
            <CreateContentField
              fieldTitle='Subscription cost'
              field={
                <TextField
                  variant='outlined'
                  type='number'
                  size='small'
                  value={localBoost.subscriptionCost}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('subscriptionCost', e.target.value)
                  }
                  error={!!errors?.subscriptionCost}
                  helperText={errors?.subscriptionCost}
                />
              }
            />

            {/* BOOST COST */}
            <CreateContentField
              fieldTitle='Boost cost'
              field={
                <TextField
                  variant='outlined'
                  type='number'
                  size='small'
                  value={localBoost.boostCost}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('boostCost', e.target.value)
                  }
                />
              }
            />

            {/* TICKETS */}
            <CreateContentField
              fieldTitle='Tickets'
              field={
                <TextField
                  variant='outlined'
                  type='number'
                  size='small'
                  value={localBoost.boostTickets}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('boostTickets', e.target.value)
                  }
                />
              }
            />

            {/* DISCOUNT */}
            <CreateContentField
              fieldTitle='Discount'
              field={
                <TextField
                  variant='outlined'
                  size='small'
                  value={localBoost.boostDiscount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('boostDiscount', e.target.value)
                  }
                  error={!!errors?.boostDiscount}
                  helperText={errors?.boostDiscount}
                />
              }
            />

            {/* DELETE */}
            <button
              type='button'
              className='content__delete-btn'
              onClick={() => onRemove(index)}
            >
              <RemoveIcon />
            </button>
          </Box>
        </div>
      </div>
    );
  },
);

export default SubscriptionBoostRow;
